<template>
    <div
        :class="cardClasses"
    >
        <div class="tw:flex tw:flex-col tw:gap-4">
            <div v-if="!hideDivision" class="tw:flex tw:justify-center">
                <div :class="divisionBadgeClasses">
                    <img
                        v-if="showDivisionImage"
                        :src="divisionImage"
                        :alt="divisionName"
                        :class="divisionImageClasses"
                        @error="divisionImageBroken = true"
                    />
                    <span
                        v-else
                        class="tw:flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-full tw:bg-slate-900 tw:text-[10px] tw:font-black tw:uppercase tw:tracking-[0.18em] tw:text-white"
                    >
                        {{ divisionInitials }}
                    </span>

                    <p class="tw:min-w-0 tw:text-center tw:text-[11px] tw:font-black tw:uppercase tw:tracking-[0.24em] tw:text-slate-500 tw:break-words">
                        {{ divisionName }}
                    </p>
                </div>
            </div>

            <div class="tw:grid tw:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] tw:items-start tw:gap-3 sm:tw:items-center sm:tw:gap-5">
                <div class="tw:flex tw:min-w-0 tw:flex-col tw:items-center tw:gap-2 tw:text-center">
                    <div :class="getLogoShellClasses(showLocalImage)">
                        <img
                            v-if="showLocalImage"
                            :src="localImage"
                            :alt="localName"
                            :class="teamImageClasses"
                            @error="localImageBroken = true"
                        />
                        <span v-else :class="['tw:text-sm tw:font-black tw:uppercase', initialsClass]">
                            {{ localInitials }}
                        </span>
                    </div>

                    <div class="tw:min-w-0 tw:space-y-1">
                        <p :class="['tw:text-[10px] tw:font-black tw:uppercase tw:tracking-[0.24em]', teamLabelClass]">
                            Local
                        </p>
                        <p :class="['tw:text-sm tw:font-bold tw:leading-tight tw:break-words', teamNameClass]">
                            {{ localName }}
                        </p>
                    </div>
                </div>

                <div class="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:self-center tw:pt-4 sm:tw:pt-0">
                    <div :class="['tw:h-6 tw:w-px', dividerClass]"></div>
                    <div :class="['tw:inline-flex tw:h-10 tw:w-10 tw:items-center tw:justify-center tw:rounded-full tw:border tw:text-[10px] tw:font-black tw:uppercase tw:tracking-widest tw:pl-[0.1rem] tw:shadow-[0_14px_30px_-24px_rgba(15,23,42,0.5)] tw:pt-1', vsBadgeClass]">
                        VS
                    </div>
                    <div :class="['tw:h-6 tw:w-px', dividerClass]"></div>
                </div>

                <div class="tw:flex tw:min-w-0 tw:flex-col tw:items-center tw:gap-2 tw:text-center">
                    <div :class="getLogoShellClasses(showAwayImage)">
                        <img
                            v-if="showAwayImage"
                            :src="awayImage"
                            :alt="awayName"
                            :class="teamImageClasses"
                            @error="awayImageBroken = true"
                        />
                        <span v-else :class="['tw:text-sm tw:font-black tw:uppercase', initialsClass]">
                            {{ awayInitials }}
                        </span>
                    </div>

                    <div class="tw:min-w-0 tw:space-y-1">
                        <p :class="['tw:text-[10px] tw:font-black tw:uppercase tw:tracking-[0.24em]', teamLabelClass]">
                            Visitante
                        </p>
                        <p :class="['tw:text-sm tw:font-bold tw:leading-tight tw:break-words', teamNameClass]">
                            {{ awayName }}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="showStatus && status" class="tw:mt-4 tw:flex tw:justify-center">
            <span class="tw:inline-flex tw:max-w-full tw:items-center tw:justify-center tw:rounded-full tw:bg-slate-900 tw:px-3 tw:py-1 tw:text-center tw:text-[11px] tw:font-semibold tw:uppercase tw:tracking-[0.24em] tw:text-white tw:break-words">
                {{ status }}
            </span>
        </div>
    </div>
</template>

<script setup>
    import { computed, ref, watch } from 'vue'

    const props = defineProps({
        localTeam: {
            type: Object,
            default: null
        },
        awayTeam: {
            type: Object,
            default: null
        },
        division: {
            type: Object,
            default: null
        },
        showStatus: {
            type: Boolean,
            default: false
        },
        status: {
            type: String,
            default: ''
        },
        compact: {
            type: Boolean,
            default: false
        },
        hideDivision: {
            type: Boolean,
            default: false
        },
        transparent: {
            type: Boolean,
            default: false
        },
        textColor: {
            type: String,
            default: 'slate'
        }
    })

    const localImageBroken = ref(false)
    const awayImageBroken = ref(false)
    const divisionImageBroken = ref(false)

    const localName = computed(() => props.localTeam?.nombre || 'Equipo local')
    const awayName = computed(() => props.awayTeam?.nombre || 'Equipo visitante')
    const divisionName = computed(() => props.division?.nombre || 'Competición')
    const localImage = computed(() => props.localTeam?.imagen || '')
    const awayImage = computed(() => props.awayTeam?.imagen || '')
    const divisionImage = computed(() => props.division?.imagen || '')

    const showLocalImage = computed(() => Boolean(localImage.value) && !localImageBroken.value)
    const showAwayImage = computed(() => Boolean(awayImage.value) && !awayImageBroken.value)
    const showDivisionImage = computed(() => Boolean(divisionImage.value) && !divisionImageBroken.value)

    const localInitials = computed(() => getInitials(localName.value))
    const awayInitials = computed(() => getInitials(awayName.value))
    const divisionInitials = computed(() => getInitials(divisionName.value, 3))
    const cardClasses = computed(() => [
        ...(props.transparent
            ? []
            : ['tw:rounded-[28px] tw:border tw:border-white/70 tw:bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(248,250,252,0.92))] tw:backdrop-blur-sm']),
        props.compact
            ? 'tw:px-4 tw:py-3'
            : 'tw:px-5 tw:py-4 tw:shadow-[0_18px_45px_-34px_rgba(15,23,42,0.45)]'
    ])
    const divisionBadgeClasses = computed(() => [
        'tw:inline-flex tw:max-w-full tw:items-center tw:gap-2 tw:rounded-full tw:border tw:border-slate-200/80 tw:bg-white/90 tw:px-3 tw:py-2 tw:shadow-[0_14px_30px_-26px_rgba(15,23,42,0.4)]',
        props.compact ? 'tw:min-h-[40px]' : 'tw:min-h-[44px]'
    ])
    const divisionImageClasses = computed(() => [
        props.compact ? 'tw:h-7 tw:w-7' : 'tw:h-8 tw:w-8',
        'tw:shrink-0 tw:object-contain'
    ])
    const teamImageClasses = computed(() => 'tw:h-full tw:w-full tw:object-contain')
    const teamLogoShellClasses = computed(() => [
        'tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-[22px]',
        props.compact ? 'tw:h-14 tw:w-14' : 'tw:h-16 tw:w-16'
    ])

    const isLightText = computed(() => props.textColor === 'white')

    const teamLabelClass = computed(() => isLightText.value
        ? 'tw:text-white/70'
        : 'tw:text-slate-400')

    const teamNameClass = computed(() => isLightText.value
        ? 'tw:text-white'
        : 'tw:text-slate-900')

    const vsBadgeClass = computed(() => isLightText.value
        ? 'tw:border-white/30 tw:bg-white/10 tw:text-white'
        : 'tw:border-slate-200 tw:bg-white tw:text-slate-500')

    const dividerClass = computed(() => isLightText.value
        ? 'tw:bg-white/20'
        : 'tw:bg-slate-200')

    const initialsClass = computed(() => isLightText.value
        ? 'tw:text-white/60'
        : 'tw:text-slate-600')

    watch(localImage, () => {
        localImageBroken.value = false
    })

    watch(awayImage, () => {
        awayImageBroken.value = false
    })

    watch(divisionImage, () => {
        divisionImageBroken.value = false
    })

    function getInitials(name, maxLength = 2) {
        const normalized = String(name || '')
            .trim()
            .split(/\s+/)
            .filter(Boolean)

        if (!normalized.length) {
            return 'N/A'
        }

        const initials = normalized.length === 1
            ? normalized[0].slice(0, maxLength).split('')
            : normalized.slice(0, maxLength).map(part => part.charAt(0))

        return initials
            .join('')
            .toUpperCase()
    }

    function getLogoShellClasses(hasImage) {
        return [
            ...teamLogoShellClasses.value,
            hasImage
                ? 'tw:bg-transparent'
                : props.transparent
                    ? 'tw:bg-white/10 tw:ring-1 tw:ring-white/20'
                    : 'tw:bg-slate-100 tw:ring-1 tw:ring-slate-200'
        ].join(' ')
    }
</script>
